import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { companyAPI } from "../../services/api";
import { CompanyState } from "../../types";

const initialState: CompanyState = {
  currentCompany: null,
  loading: false,
  error: null,
  countries: [],
  countriesLoading: false,
  companyStats: null,
};

// Get Company
export const getCompany = createAsyncThunk(
  "company/getCompany",
  async (_, { rejectWithValue }) => {
    try {
      const response = await companyAPI.getCompany();
      return response.data.data.company;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch company",
      );
    }
  },
);

// Get Countries
export const getCountries = createAsyncThunk(
  "company/getCountries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await companyAPI.getCountries();
      const countries = response.data.data.countries;
      // Cache for offline use (best-effort)
      const { cacheCountries } = await import("../../utils/indexedDB");
      await cacheCountries(countries).catch((err) => {
        console.warn("[Offline] Failed to cache countries:", err);
      });
      return countries;
    } catch (error: any) {
      // If offline, try cache
      if (!navigator.onLine || error.code === "ERR_NETWORK") {
        try {
          const { getCachedCountries } = await import("../../utils/indexedDB");
          const cached = await getCachedCountries();
          if (cached && cached.length > 0) {
            // console.log("[Offline] Serving countries from cache:", cached.length);
            return cached;
          }
        } catch (cacheErr) {
          console.error(
            "[Offline] Countries cache retrieval failed:",
            cacheErr,
          );
        }
      }
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch countries",
      );
    }
  },
  {
    /**
     * One fetch per session, however many callers ask.
     *
     * The country list is static reference data, and half a dozen components
     * want it — the route guard on every navigation, each expanded container
     * panel, every form. They already tried to be polite with
     * `if (!countries.length)`, but that only helps once a fetch has *landed*:
     * panels mounting together all saw an empty list and all asked, so a single
     * page load spent five identical requests on data that never changes.
     * Declining while one is already in flight is what actually collapses them.
     */
    condition: (_, { getState }) => {
      const { countries, countriesLoading } = (
        getState() as { company: CompanyState }
      ).company;
      return !countries?.length && !countriesLoading;
    },
  },
);

// Get Company Stats
export const getCompanyStats = createAsyncThunk(
  "company/getCompanyStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await companyAPI.getCompanyStats();
      return response.data.data; // totalUsers, pendingInvites, adminUsers
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch company stats",
      );
    }
  },
);

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentOrganization: (state) => {
      state.currentCompany = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Countries
      .addCase(getCountries.pending, (state) => {
        state.loading = true;
        // Tracked apart from `loading`, which every company request shares:
        // the guard above has to know whether *this* fetch is outstanding.
        state.countriesLoading = true;
        state.error = null;
      })
      .addCase(getCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.countriesLoading = false;
        state.countries = action.payload;
        state.error = null;
      })
      .addCase(getCountries.rejected, (state, action) => {
        state.loading = false;
        // Cleared, not latched: a failed read should be retried by the next
        // caller rather than leaving the app without countries for good.
        state.countriesLoading = false;
        state.error = action.payload as string;
      })

      // Get Company
      .addCase(getCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCompany = action.payload;
        state.error = null;
      })
      .addCase(getCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get Company Stats
      .addCase(getCompanyStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCompanyStats.fulfilled, (state, action) => {
        state.loading = false;
        state.companyStats = action.payload;
        state.error = null;
      })
      .addCase(getCompanyStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentOrganization } = companySlice.actions;
export default companySlice.reducer;
